import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LuTriangleAlert, LuPower, LuShieldAlert } from "react-icons/lu";
import { api, ApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/ui/page-header";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

/**
 * Maintenance mode toggle. Flipping the switch writes to KV on the
 * public worker; the gate takes effect within ~30 seconds (per-isolate
 * cache window). The console itself is unaffected because it lives on
 * a separate worker, which is the whole reason for the split.
 */
export function MaintenancePage() {
  const qc = useQueryClient();
  const flag = useQuery({
    queryKey: ["console", "admin", "maintenance"],
    queryFn: () => api.adminMaintenance.get(),
  });

  const [message, setMessage] = useState("");
  const [until, setUntil] = useState("");
  const [confirmEnable, setConfirmEnable] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);

  // Hydrate local state from the server flag once the query lands.
  useEffect(() => {
    if (flag.data) {
      setMessage(flag.data.maintenance.message ?? "");
      setUntil(flag.data.maintenance.until ?? "");
    }
  }, [flag.data]);

  const set = useMutation({
    mutationFn: (body: { enabled: boolean; message?: string; until?: string | null }) =>
      api.adminMaintenance.set(body),
    onSuccess: (_data, vars) => {
      toast.success(vars.enabled ? "Maintenance mode ON." : "Maintenance mode OFF.");
      setConfirmEnable(false);
      setConfirmDisable(false);
      qc.invalidateQueries({ queryKey: ["console", "admin", "maintenance"] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Failed."),
  });

  const enabled = flag.data?.maintenance.enabled ?? false;

  return (
    <>
      <PageHeader
        icon={LuShieldAlert}
        title="Maintenance mode"
        description="Force the public site and API into a read-only maintenance state. Operators (this console) and inbound webhooks/email keep working."
      />

      <div className="p-8 space-y-6 max-w-2xl">
        {/* Status banner */}
        <Card>
          <div className="px-5 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span
                className={`w-2.5 h-2.5 rounded-full ${
                  enabled ? "bg-[#fca5a5]" : "bg-[#6ee7b7]"
                }`}
              />
              <div>
                <p className="text-sm font-semibold text-[var(--color-text)]">
                  {flag.isLoading
                    ? "Loading..."
                    : enabled
                    ? "Maintenance mode is ON"
                    : "Service is live"}
                </p>
                {flag.data?.maintenance.updatedAt && (
                  <p className="text-[11px] text-[var(--color-text-subtle)] tabular-nums">
                    Last changed {new Date(flag.data.maintenance.updatedAt).toLocaleString()}
                    {flag.data.maintenance.updatedBy ? ` by ${flag.data.maintenance.updatedBy}` : ""}
                  </p>
                )}
              </div>
            </div>
            {enabled ? (
              <Button
                variant="secondary"
                onClick={() => setConfirmDisable(true)}
                disabled={set.isPending}
              >
                <LuPower className="w-4 h-4 mr-1.5" /> Turn OFF
              </Button>
            ) : (
              <Button
                onClick={() => setConfirmEnable(true)}
                disabled={set.isPending}
                className="bg-[rgba(237,66,69,0.15)] border-[rgba(237,66,69,0.4)] text-[#fca5a5] hover:bg-[rgba(237,66,69,0.25)]"
              >
                <LuTriangleAlert className="w-4 h-4 mr-1.5" /> Turn ON
              </Button>
            )}
          </div>
        </Card>

        {/* Message + ETA editor — saving here while ENABLED applies live. */}
        <Card>
          <div className="px-5 py-4 border-b border-[var(--color-border)]">
            <p className="text-sm font-semibold text-[var(--color-text)]">Maintenance message</p>
            <p className="text-[11px] text-[var(--color-text-subtle)]">
              Shown to users in the 503 response and the SPA banner. Keep it short - this is what visitors see.
            </p>
          </div>
          <div className="px-5 py-4 space-y-3">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="DESTROY.NETWORK is undergoing scheduled maintenance and will be back shortly."
              className="w-full px-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] placeholder:text-[var(--color-text-subtle)] focus:outline-none focus:border-[var(--color-accent)] resize-none"
            />
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-[var(--color-text-subtle)] mb-1">
                Estimated end (optional, ISO-8601)
              </label>
              <Input
                type="datetime-local"
                value={until ? until.slice(0, 16) : ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setUntil(v ? new Date(v).toISOString() : "");
                }}
              />
            </div>
            <div className="flex justify-end">
              <Button
                variant="secondary"
                onClick={() =>
                  set.mutate({
                    enabled,
                    message,
                    until: until || null,
                  })
                }
                disabled={set.isPending || flag.isLoading}
              >
                Save message
              </Button>
            </div>
          </div>
        </Card>

        <Card>
          <div className="px-5 py-4 text-[11px] text-[var(--color-text-subtle)] space-y-1.5">
            <p className="font-semibold text-[var(--color-text-muted)]">When ON, these stay live:</p>
            <ul className="list-disc pl-4 space-y-0.5">
              <li>Operator console (this site) - separate worker, never gated</li>
              <li>/api/health - uptime probes</li>
              <li>/api/internal/admin/* - console-api proxy back to product DB</li>
            </ul>
            <p className="font-semibold text-[var(--color-text-muted)] pt-2">Blocked (everything else):</p>
            <ul className="list-disc pl-4 space-y-0.5">
              <li>Magic-code login + verify</li>
              <li>Inbox CRUD, messages, polling</li>
              <li>Public API (SDK, RapidAPI proxy)</li>
              <li>Billing portal, account screens</li>
              <li>Stripe webhooks (Stripe retries automatically)</li>
              <li>Inbound email forwarder (senders requeue)</li>
              <li>Legacy /api/admin/* session path</li>
            </ul>
          </div>
        </Card>
      </div>

      <ConfirmDialog
        open={confirmEnable}
        title="Enable maintenance mode?"
        description={`The entire public API and login flow will start returning 503 within ~30 seconds. ${
          message ? `Message shown: "${message}".` : "No custom message set - the default text will be shown."
        }`}
        confirmLabel="Enable"
        destructive
        pending={set.isPending}
        onClose={() => setConfirmEnable(false)}
        onConfirm={() =>
          set.mutate({ enabled: true, message, until: until || null })
        }
      />
      <ConfirmDialog
        open={confirmDisable}
        title="Disable maintenance mode?"
        description="The public site and API will be reachable again within ~30 seconds."
        confirmLabel="Disable"
        pending={set.isPending}
        onClose={() => setConfirmDisable(false)}
        onConfirm={() =>
          set.mutate({ enabled: false, message, until: until || null })
        }
      />
    </>
  );
}
